import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AddSalarySettings from './AddSalarySettings';
import EditSalarySettings from './EditSalarySettings';
import ImportSalarySettings from './ImportSalarySettings';

const SalarySettings = () => {
  const [showImportModal, setShowImportModal] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [salarySettings, setSalarySettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedSettingId, setSelectedSettingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [sortBy, setSortBy] = useState('name');
  const [salaryTemplates, setSalaryTemplates] = useState([]);
  const [userPermissions, setUserPermissions] = useState({
    can_view: false,
    can_add: false,
    can_edit: false,
    can_delete: false,
    loaded: false
  });

  const navigate = useNavigate();
  const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

  // Fetch user permissions
  const fetchUserPermissions = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setUserPermissions({
          can_view: false,
          can_add: false,
          can_edit: false,
          can_delete: false,
          loaded: true
        });
        return;
      }

      const response = await axios.get(`${API_BASE}/get_user_permissions.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.permissions) {
        const salarySettingsPermissions = response.data.permissions.find(
          p => p.module_name === 'SalarySettings'
        );

        if (salarySettingsPermissions) {
          setUserPermissions({
            can_view: Boolean(salarySettingsPermissions.can_view),
            can_add: Boolean(salarySettingsPermissions.can_add),
            can_edit: Boolean(salarySettingsPermissions.can_edit),
            can_delete: Boolean(salarySettingsPermissions.can_delete),
            loaded: true
          });
        } else {
          // Default permissions if module not found
          setUserPermissions({
            can_view: true,
            can_add: true,
            can_edit: true,
            can_delete: true,
            loaded: true
          });
        }
      } else {
        // Default permissions on error
        setUserPermissions({
          can_view: true,
          can_add: true,
          can_edit: true,
          can_delete: true,
          loaded: true
        });
      }
    } catch (error) {
      // Default permissions on exception
      setUserPermissions({
        can_view: true,
        can_add: true,
        can_edit: true,
        can_delete: true,
        loaded: true
      });
    }
  }, [API_BASE]);

  const fetchEmployees = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('No authentication token found');
        return;
      }

      const response = await axios.get(`${API_BASE}/get_employees.php`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 10000
      });

      if (response.data && response.data.success) {
        setEmployees(response.data.employees || []);
      } else {
        setEmployees([]);
      }
    } catch (error) {
      setEmployees([]);
      console.error('Error fetching employees:', error);
    }
  }, [API_BASE]);

  const fetchSalarySettings = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('No authentication token found');
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API_BASE}salarysettings/get_salary_settings.php`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 10000
      });

      if (response.data && response.data.success) {
        setSalarySettings(response.data.salary_settings || []);
        setError('');
      } else {
        setSalarySettings([]);
        const errorMsg = response.data?.error || 'Failed to fetch salary settings';
        setError(errorMsg);
      }
    } catch (error) {
      setSalarySettings([]);
      const errorMsg = error.response?.data?.error ||
        error.message ||
        'Failed to fetch salary settings. Please check your connection.';
      setError(errorMsg);
      console.error('Error fetching salary settings:', error);
    } finally {
      setLoading(false);
    }
  }, [API_BASE]);

  const fetchSalaryTemplates = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return;
      }

      const response = await axios.get(`${API_BASE}/get_salary_templates.php`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 10000
      });

      if (response.data && response.data.success) {
        setSalaryTemplates(response.data.templates?.filter(t => t.is_active) || []);
      } else {
        setSalaryTemplates([]);
      }
    } catch (error) {
      setSalaryTemplates([]);
      console.error('Error fetching salary templates:', error);
    }
  }, [API_BASE]);

  useEffect(() => {
    const initializeData = async () => {
      try {
        await fetchUserPermissions();
        await Promise.all([
          fetchEmployees(),
          fetchSalarySettings(),
          fetchSalaryTemplates()
        ]);
      } catch (error) {
        console.error('Initialization error:', error);
        setError(`Initialization failed: ${error.message}`);
      }
    };

    initializeData();
  }, [fetchEmployees, fetchSalarySettings, fetchSalaryTemplates, fetchUserPermissions]);

  // Permission check functions
  const canViewSalarySettings = () => userPermissions.can_view;
  const canAddSalarySetting = () => userPermissions.can_add;
  const canEditSalarySetting = () => userPermissions.can_edit;
  const canDeleteSalarySetting = () => userPermissions.can_delete;
  const canImportSalarySettings = () => userPermissions.can_add;

  const handleAdd = (employee) => {
    if (!canAddSalarySetting()) {
      alert('You do not have permission to add salary settings');
      return;
    }
    // Check if employee already has salary settings
    const existingSetting = salarySettings.find(setting => setting.emp_id === employee.emp_id);
    if (existingSetting) {
      alert('This employee already has salary settings. Please edit the existing settings.');
      return;
    }
    setSelectedEmployee(employee);
    setShowAddModal(true);
  };

  const handleEdit = (salarySetting, employee) => {
    if (!canEditSalarySetting()) {
      alert('You do not have permission to edit salary settings');
      return;
    }
    setSelectedSettingId(salarySetting.setting_id);
    setSelectedEmployee(employee);
    setShowEditModal(true);
  };

  const handleDelete = async (settingId, empId) => {
    if (!canDeleteSalarySetting()) {
      alert('You do not have permission to delete salary settings');
      return;
    }

    if (window.confirm('Are you sure you want to delete these salary settings?')) {
      try {
        const token = localStorage.getItem('authToken');
        const response = await axios.post(`${API_BASE}salarysettings/delete_salary_settings.php`,
          { setting_id: settingId },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data.success) {
          await fetchSalarySettings();
          alert('Salary settings deleted successfully');
        } else {
          setError(response.data.error || 'Failed to delete salary settings');
        }
      } catch (error) {
        const errorMsg = error.response?.data?.error || 'Failed to delete salary settings';
        setError(errorMsg);
        console.error('Error deleting salary settings:', error);
      }
    }
  };

  const handleImport = async (records, progressCallback) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const totalRecords = records.length;
      let processed = 0;
      const batchSize = 50;

      for (let i = 0; i < records.length; i += batchSize) {
        const batch = records.slice(i, i + batchSize);

        const response = await axios.post(
          `${API_BASE}imports/import_salary_settings.php`,
          { records: batch },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.data.success) {
          throw new Error(response.data.error || 'Import failed');
        }

        processed += batch.length;
        const progress = Math.round((processed / totalRecords) * 100);
        progressCallback(progress);
      }

      await Promise.all([fetchSalarySettings(), fetchEmployees()]);
      setError('');
    } catch (error) {
      console.error('Import salary settings error:', error);
      throw error;
    }
  };

  const getSalarySettingForEmployee = (empId) => {
    return salarySettings.find(setting => setting.emp_id === empId);
  };

  const getSalaryTypeBadge = (salaryType) => {
    switch (salaryType) {
      case 'Monthly':
        return <span className="badge bg-primary">Monthly</span>;
      case 'Daily':
        return <span className="badge bg-info">Daily</span>;
      case 'Hourly':
        return <span className="badge bg-warning">Hourly</span>;
      case 'Piece':
        return <span className="badge bg-secondary">Piece</span>;
      default:
        return <span className="badge bg-light text-dark">{salaryType}</span>;
    }
  };

  const getYesNoBadge = (value) => {
    return value ? <span className="badge bg-success">Yes</span> : <span className="badge bg-danger">No</span>;
  };

  const filteredEmployees = employees.filter(employee => {
    const matchesSearch =
      employee.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.emp_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.email?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus === 'All' || employee.status === filterStatus;

    return matchesSearch && matchesStatus;
  });

  const sortedEmployees = [...filteredEmployees].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.first_name.localeCompare(b.first_name);
      case 'code':
        return a.emp_code.localeCompare(b.emp_code);
      case 'status':
        return a.status.localeCompare(b.status);
      default:
        return a.emp_id - b.emp_id;
    }
  });

  // Loading state
  if ((loading && employees.length === 0) || !userPermissions.loaded) {
    return (
      <div className="container-fluid py-4">
        <div className="row">
          <div className="col-12">
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3 text-muted">Loading salary settings...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Permission denied state
  if (!canViewSalarySettings()) {
    return (
      <div className="container-fluid py-4">
        <div className="row">
          <div className="col-12">
            <div className="card shadow-sm border-0">
              <div className="card-body text-center py-5">
                <i className="bi bi-shield-exclamation display-1 text-warning"></i>
                <h3 className="mt-3 text-warning">Access Denied</h3>
                <p className="text-muted">You do not have permission to view salary settings.</p>
                <button
                  className="btn btn-primary mt-3"
                  onClick={() => navigate('/admin-panel')}
                >
                  <i className="bi bi-speedometer2 me-2"></i>
                  Back to Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-body py-4">
              <div className="row align-items-center">
                <div className="col-md-6">
                  <div className="d-flex align-items-center">
                    <div className="btn-group me-3" role="group">
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => navigate('/admin-panel')}
                        title="Go to Admin Panel"
                      >
                        <i className="bi bi-speedometer2"></i>
                      </button>
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => navigate('/Menu')}
                        title="Go to Menu"
                      >
                        <i className="bi bi-menu-button"></i>
                      </button>
                    </div>
                    <div>
                      <h2 className="h4 mb-1 fw-bold text-dark">
                        <i className="bi bi-gear me-2 text-primary"></i>
                        Employee Settings.
                      </h2>
                      <p className="text-muted mb-0">
                        Configure settings for employees
                      </p>
                      <small className="text-muted">
                        Employees: {employees.length} | Settings Configured: {salarySettings.filter(s => s.setting_id).length}
                      </small>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 text-end">
                  <div className="d-flex justify-content-end align-items-center">
                    <button
                      className="btn btn-outline-success me-2"
                      onClick={() => setShowImportModal(true)}
                      disabled={!canImportSalarySettings()}
                    >
                      <i className="bi bi-upload me-2"></i>
                      Import
                    </button>
                    <button
                      className="btn btn-outline-secondary me-2"
                      onClick={() => navigate('/dashboard')}
                    >
                      <i className="bi bi-arrow-left me-2"></i>
                      Back to Dashboard
                    </button>
                    <button
                      className="btn btn-outline-primary"
                      onClick={() => navigate('/Menu')}
                    >
                      <i className="bi bi-file-earmark-text me-2"></i>
                      Menu
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Navigation */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card bg-light border-0">
            <div className="card-body py-3">
              <div className="row text-center">
                <div className="col-md-3 mb-2 mb-md-0">
                  <button
                    className="btn btn-outline-primary w-100 d-flex align-items-center justify-content-center"
                    onClick={() => navigate('/admin-panel')}
                  >
                    <i className="bi bi-speedometer2 me-2"></i>
                    Dashboard
                  </button>
                </div>
                <div className="col-md-3 mb-2 mb-md-0">
                  <button
                    className="btn btn-outline-success w-100 d-flex align-items-center justify-content-center"
                    onClick={() => navigate('/employees')}
                  >
                    <i className="bi bi-people me-2"></i>
                    Employees
                  </button>
                </div>
                <div className="col-md-3 mb-2 mb-md-0">
                  <button
                    className="btn btn-outline-info w-100 d-flex align-items-center justify-content-center"
                    onClick={() => navigate('/salary-templates')}
                  >
                    <i className="bi bi-file-earmark-text me-2"></i>
                    Templates
                  </button>
                </div>
                <div className="col-md-3 mb-2 mb-md-0">
                  <button
                    className="btn btn-primary w-100 d-flex align-items-center justify-content-center"
                    onClick={() => navigate('/admin-panel')}
                  >
                    <i className="bi bi-speedometer2 me-2"></i>
                    Admin Panel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="alert alert-danger d-flex align-items-center" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              <div>
                <strong>Error:</strong> {error}
                <div className="mt-2">
                  <button
                    className="btn btn-sm btn-outline-danger me-2"
                    onClick={fetchSalarySettings}
                  >
                    <i className="bi bi-arrow-clockwise me-1"></i>
                    Retry
                  </button>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setError('')}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <div className="row g-3 align-items-center">
                <div className="col-md-5">
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0">
                      <i className="bi bi-search text-muted"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0"
                      placeholder="Search employees..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <button
                        className="btn btn-outline-secondary border-start-0"
                        onClick={() => setSearchTerm('')}
                      >
                        <i className="bi bi-x"></i>
                      </button>
                    )}
                  </div>
                </div>
                <div className="col-md-3">
                  <select
                    className="form-select"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <option value="All">All Status</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Resigned">Resigned</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <select
                    className="form-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="name">Sort by Name</option>
                    <option value="code">Sort by Employee Code</option>
                    <option value="status">Sort by Status</option>
                  </select>
                </div>
                <div className="col-md-1 text-end">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={fetchSalarySettings}
                    title="Refresh"
                  >
                    <i className="bi bi-arrow-clockwise"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Employees List with Salary Settings */}
      <div className="row">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-body p-0">
              {sortedEmployees.length === 0 ? (
                <div className="text-center py-5">
                  <i className="bi bi-people display-1 text-muted"></i>
                  <h4 className="mt-3 text-muted">No employees found</h4>
                  <p className="text-muted">
                    {employees.length === 0
                      ? "No employees available. Please add employees first."
                      : "No employees match your search criteria."
                    }
                  </p>
                  {employees.length === 0 && (
                    <button
                      className="btn btn-primary mt-2"
                      onClick={() => navigate('/employees')}
                    >
                      <i className="bi bi-plus-circle me-2"></i>
                      Add Employees
                    </button>
                  )}
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="ps-4">Employee</th>
                        <th>Status</th>
                        <th>Salary Settings</th>
                        <th className="text-end pe-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedEmployees.map((employee) => {
                        const salarySetting = getSalarySettingForEmployee(employee.emp_id);
                        const hasSettings = salarySetting && salarySetting.setting_id;

                        return (
                          <tr key={employee.emp_id} className={hasSettings ? 'table-success' : ''}>
                            <td className="ps-4">
                              <div className="d-flex align-items-center">
                                <div className="employee-icon bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                                  style={{ width: '40px', height: '40px' }}
                                >
                                  <i className="bi bi-person"></i>
                                </div>
                                <div>
                                  <div className="fw-semibold">
                                    {employee.first_name} {employee.last_name}
                                    {hasSettings && (
                                      <i className="bi bi-check-circle-fill text-success ms-2" title="Salary settings configured"></i>
                                    )}
                                  </div>
                                  <small className="text-muted">
                                    ID: {employee.emp_id} | Code: {employee.emp_code}
                                  </small>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className={`badge ${employee.status === 'Active' ? 'bg-success' :
                                employee.status === 'Inactive' ? 'bg-secondary' :
                                  'bg-danger'
                                }`}>
                                {employee.status || 'Unknown'}
                              </span>
                            </td>
                            <td>
                              {hasSettings ? (
                                <div>
                                  <div className="mb-1">
                                    <small className="text-muted">Salary Type: </small>
                                    {getSalaryTypeBadge(salarySetting.salary_type)}
                                  </div>
                                  <div className="mb-1">
                                    <small className="text-muted">Salary: </small>
                                    <strong>₹{parseFloat(salarySetting.salary_value).toFixed(2)}</strong>
                                  </div>
                                  <div className="mb-1">
                                    <small className="text-muted">PF: </small>
                                    {getYesNoBadge(salarySetting.eligible_for_pf)}
                                    {salarySetting.eligible_for_pf && (
                                      <small className="text-muted ms-1">({salarySetting.pf_percentage}%)</small>
                                    )}
                                  </div>
                                  <div>
                                    <small className="text-muted">ESI: </small>
                                    {getYesNoBadge(salarySetting.eligible_for_esi)}
                                  </div>
                                </div>
                              ) : (
                                <span className="badge bg-warning">No Settings</span>
                              )}
                            </td>
                            <td className="text-end pe-4">
                              <div className="btn-group" role="group">
                                {hasSettings ? (
                                  <>
                                    {canEditSalarySetting() && (
                                      <button
                                        className="btn btn-outline-primary btn-sm"
                                        onClick={() => handleEdit(salarySetting, employee)}
                                        title="Edit Settings"
                                      >
                                        <i className="bi bi-pencil"></i>
                                      </button>
                                    )}
                                    {canDeleteSalarySetting() && (
                                      <button
                                        className="btn btn-outline-danger btn-sm"
                                        onClick={() => handleDelete(salarySetting.setting_id, employee.emp_id)}
                                        title="Delete Settings"
                                      >
                                        <i className="bi bi-trash"></i>
                                      </button>
                                    )}
                                  </>
                                ) : (
                                  canAddSalarySetting() && (
                                    <button
                                      className="btn btn-outline-success btn-sm"
                                      onClick={() => handleAdd(employee)}
                                      title="Add Settings"
                                    >
                                      <i className="bi bi-plus"></i>
                                    </button>
                                  )
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Footer */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card bg-light border-0">
            <div className="card-body py-3">
              <div className="row text-center">
                <div className="col-md-4">
                  <div className="fw-bold text-primary fs-4">{employees.length}</div>
                  <small className="text-muted">Total Employees</small>
                </div>
                <div className="col-md-4">
                  <div className="fw-bold text-success fs-4">
                    {salarySettings.filter(s => s.setting_id).length}
                  </div>
                  <small className="text-muted">Configured Settings</small>
                </div>
                <div className="col-md-4">
                  <div className="fw-bold text-warning fs-4">
                    {employees.length - salarySettings.filter(s => s.setting_id).length}
                  </div>
                  <small className="text-muted">Pending Settings</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showAddModal && selectedEmployee && canAddSalarySetting() && (
        <AddSalarySettings
          show={showAddModal}
          onHide={() => setShowAddModal(false)}
          employee={selectedEmployee}
          salaryTemplates={salaryTemplates}
          employees={employees}
          onSalarySettingAdded={fetchSalarySettings}
        />
      )}

      {showEditModal && selectedSettingId && selectedEmployee && canEditSalarySetting() && (
        <EditSalarySettings
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          settingId={selectedSettingId}
          employee={selectedEmployee}
          onSalarySettingUpdated={fetchSalarySettings}
        />
      )}

      {showImportModal && (
        <ImportSalarySettings
          show={showImportModal}
          onHide={() => setShowImportModal(false)}
          onImport={handleImport}
          canImportSalary={canImportSalarySettings()}
          error={error}
          setError={setError}
          API_BASE={API_BASE}
        />
      )}
    </div>
  );
};

export default SalarySettings;
